import type { ChatParticipant, Person } from '../common'

import cx from 'classnames'
import { useState } from 'react'
import { animated, useSprings } from 'react-spring'

import {
  // formatLastMessageDateTime,
  generateColor, getInitials
} from '../common'

export interface Participant extends Person {
  isPatient: boolean
  readAt?: Date
}

export interface ParticipantsAvatarsProps extends React.HTMLAttributes<HTMLDivElement> {
  participants: ChatParticipant[]
  excludedIds?: string[]
  direction?: 'left' | 'right'
}

export function ParticipantsAvatars ({ participants, excludedIds = [], direction = 'left', className, ...rest }: ParticipantsAvatarsProps): JSX.Element | null {
  let max = 3
  let [ isHoveringParticipants, setIsHoveringParticipants ] = useState(false)

  participants = participants.filter(p => !excludedIds.includes(p.id))

  let firstParticipants = participants.length > max + 1
    ? participants.slice(0, max)
    : participants.slice(0, max + 1)

  let restParticipants = participants.slice(firstParticipants.length)
  let badgesCount = firstParticipants.length + Math.min(restParticipants.length, 1)

  let [ springs ] = useSprings(badgesCount, i => ({
    transform: isHoveringParticipants
      ? direction === 'left'
        ? `translateX(${(badgesCount - i - 1) * 4}px)`
        : `translateX(${i * -4}px)`

      : direction === 'left'
        ? `translateX(${(badgesCount - i - 1) * 12}px)`
        : `translateX(${i * -12}px)`
  }), [ badgesCount, isHoveringParticipants ])

  if (!firstParticipants.length) {
    return null
  }

  return (
    <div
      className={cx('flex flex-row items-center p-1 -m-1', className)}
      onMouseEnter={() => {
        setIsHoveringParticipants(true)
      }}
      onMouseLeave={() => {
        setIsHoveringParticipants(false)
      }}
      {...rest}
    >
      {firstParticipants.map((participant, i) => (
        <animated.div
          key={participant.id}
          className={cx(
            'w-6 h-6 rounded-full flex-shrink-0 text-[10px] font-bold flex items-center justify-center shadow will-change-transform',
            generateColor(participant.id),
            direction === 'left' ? 'left-0' : 'right-0'
          )}
          style={springs[i]}
          title={getAvatarTooltip(participant)}
        >
          {getInitials(participant)}
        </animated.div>
      ))}

      {restParticipants.length > 0 && (
        <animated.div
          className={cx('w-6 h-6 rounded-full flex-shrink-0 text-[10px] font-bold flex items-center justify-center shadow will-change-transform bg-gray-200')}
          style={springs[springs.length - 1]}
          title={restParticipants.map(getAvatarTooltip).join('\n')}
        >
          +{restParticipants.length}
        </animated.div>
      )}
    </div>
  )
}

function getAvatarTooltip (participant: ChatParticipant): string {
  let tooltip = participant.name

  if (participant.type === 'patient') {
    tooltip += ' (patient)'
  }

  // Todo: make it work
  // if (participant.readAt && !isNaN(participant.readAt.getTime())) {
  //   let formatted = formatLastMessageDateTime(participant.readAt)
  //   let preposition = formatted.includes(':') ? 'at' : 'on'
  //
  //   tooltip += `, read ${preposition} ${formatted}`
  // }

  return tooltip
}
